import { useUi } from '../stores/ui.js';
import { useNotifications } from '../hooks/useNotifications.js';
import { nativeStatusLine, useNativePush } from '../hooks/useNativePush.js';
import { Sheet } from '../ui/Sheet.js';
import { Button } from '../ui/Button.js';
import { InstallPromptCard } from './InstallPromptCard.js';
import { inNativeShell, nativePushAvailable } from '../lib/native.js';

export function NotificationsModal(): React.ReactElement {
  const open = useUi((s) => s.notificationsOpen);
  const close = useUi((s) => s.closeNotifications);

  return (
    <Sheet open={open} onClose={close} title="Notifications">
      <div className="pile">
        <p className="discret">
          No scheduled reminders. You get a notification when a Carrefour run finishes, and when
          a message is sent to you — every one of them is kept in Messages.
        </p>
        {/* Dans la coque Android, la WebView n'a pas de PushManager : le push est natif (spec 18). */}
        {!inNativeShell ? (
          <WebNotifications />
        ) : nativePushAvailable ? (
          <NativeNotifications open={open} />
        ) : (
          <p className="discret" role="status">
            Update the Nota app (latest nota.apk) to get notifications on this phone.
          </p>
        )}
      </div>
    </Sheet>
  );
}

function NativeNotifications({ open }: { open: boolean }): React.ReactElement {
  const { status, busy, waiting, error, enable, disable, openSettings } = useNativePush(open);
  const noDistributor = status !== null && !status.distributor;

  return (
    <>
      {noDistributor && (
        <ol className="discret pl-5 list-decimal space-y-1.5">
          <li>Install ntfy from F-Droid.</li>
          <li>In Android settings, exclude ntfy from battery optimization — otherwise notifications arrive late.</li>
          <li>Come back here and tap Enable.</li>
        </ol>
      )}

      {status?.permission === 'denied' && (
        <Button variant="secondary" className="bouton-large" onClick={openSettings}>
          Open Android settings
        </Button>
      )}

      {/* Abonné mais permission retirée : Disable reste, sinon le serveur pousse dans le vide. */}
      {status?.subscribed ? (
        <Button variant="danger" className="bouton-large" loading={busy} onClick={() => void disable()}>
          Disable
        </Button>
      ) : status?.permission === 'denied' ? null : (
        <Button
          className="bouton-large"
          disabled={status === null}
          loading={busy || waiting}
          onClick={() => void enable()}
        >
          Enable on this device
        </Button>
      )}

      <p className="discret" role="status">
        {nativeStatusLine(status, waiting, error)}
      </p>
    </>
  );
}

function WebNotifications(): React.ReactElement {
  const {
    isSupported,
    isStandalone,
    needsInstall,
    permission,
    subscription,
    subscribing,
    error,
    enabled,
    requestPermission,
    unsubscribe,
  } = useNotifications();

  const statusLine = deriveStatus({ isSupported, needsInstall, permission, subscription, subscribing, error });

  return (
    <>
      {needsInstall && <InstallPromptCard dismissible={false} />}
      {/* Hors iOS, un onglet s'abonne sans installation : on dit ce que ça implique. */}
      {isSupported && !isStandalone && !needsInstall && (
        <p className="discret">
          Notifications are delivered to this browser, as long as it runs. Install Nota from the
          browser menu to get them as an app.
        </p>
      )}

      {!enabled ? (
        <Button
          className="bouton-large"
          disabled={!isSupported || needsInstall || permission === 'denied'}
          loading={subscribing}
          onClick={() => void requestPermission()}
        >
          Enable on this device
        </Button>
      ) : (
        <Button variant="danger" className="bouton-large" onClick={() => void unsubscribe()}>
          Disable
        </Button>
      )}

      <p className="discret" role="status">
        {statusLine}
      </p>
    </>
  );
}

function deriveStatus({
  isSupported,
  needsInstall,
  permission,
  subscription,
  subscribing,
  error,
}: {
  isSupported: boolean;
  needsInstall: boolean;
  permission: NotificationPermission;
  subscription: PushSubscription | null;
  subscribing: boolean;
  error: string | null;
}): string {
  if (!isSupported) return 'Notifications are not supported on this device.';
  if (needsInstall) return 'Install Nota to your home screen first.';
  if (error) return error;
  if (subscribing) return 'Enabling…';
  if (permission === 'denied') return 'Blocked. Allow notifications for Nota in your system or browser settings.';
  if (permission === 'default') return 'Tap Enable to allow notifications.';
  if (permission === 'granted' && !subscription) return 'Permission granted but subscription missing. Try again.';
  return 'Notifications are on.';
}
